import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocSection,
  DocCard,
  DocAlert,
  DocCodeBlock,
  DocNavFooter,
  DocLink,
} from "@/components/docs";
import { docsColors, docsComponents } from "@/lib/docs-theme";

export default function PingMonitoringPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "Monitor Types", href: "/docs/monitors" },
    { label: "Ping Monitoring" },
  ];

  return (
    <DocLayout
      title="Ping Monitoring (ICMP) - Uptime Monitor Documentation"
      description="Learn how to monitor server availability using ICMP ping. Check connectivity, response times, and packet loss."
      canonical="https://uptime-monitor.io/docs/monitors/ping"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Ping Monitoring (ICMP)"
        description="Monitor server and network device availability using ICMP ping. The most basic and reliable way to check if a host is reachable."
      />

      {/* Overview */}
      <DocSection>
        <DocAlert variant="info" className="p-6">
          <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
            What is Ping Monitoring?
          </h2>
          <p className={`${docsColors.text.body} mb-4`}>
            Ping monitoring uses the Internet Control Message Protocol (ICMP) to check if a host is
            reachable on the network. It sends echo request packets and waits for echo reply
            packets, measuring response time and packet loss.
          </p>
          <p className={docsColors.text.body}>
            This is the most fundamental connectivity test, working at the network layer without
            requiring any specific services or ports to be running on the target host.
          </p>
        </DocAlert>
      </DocSection>

      {/* Configuration */}
      <DocSection title="Configuration">
        <DocCard>
          <div className="space-y-6">
            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Host</h3>
              <p className={`${docsColors.text.body} mb-3`}>
                Specify the hostname or IP address to ping. Domain names will be resolved to IP
                addresses.
              </p>
              <DocCodeBlock className="leading-relaxed">
                Format: ping://hostname
                <br />
                <br />
                Examples:
                <br />
                • ping://example.com
                <br />
                • ping://192.168.1.1
                <br />
                • ping://server.internal.network
                <br />• ping://8.8.8.8
              </DocCodeBlock>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Check Frequency</h3>
              <p className={docsColors.text.body}>
                How often to ping the host. Options range from 10 seconds to 60 minutes. For
                critical infrastructure, consider more frequent checks.
              </p>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Timeout Settings</h3>
              <p className={docsColors.text.body}>
                Maximum time to wait for a ping response. Default is typically 5 seconds. Increase
                for hosts with high latency or unreliable connections.
              </p>
            </div>
          </div>
        </DocCard>
      </DocSection>

      {/* What Ping Monitors */}
      <DocSection title="What Ping Monitoring Checks">
        <div className="grid gap-4">
          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Host Reachability</h3>
            <p className={docsColors.text.body}>
              Verifies that the host is online and responding to network requests. This is the most
              basic test of whether a server or device is operational.
            </p>
          </DocCard>

          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
              Response Time (Latency)
            </h3>
            <p className={docsColors.text.body}>
              Measures round-trip time (RTT) for packets to reach the host and return. Helps
              identify network performance issues and connectivity degradation.
            </p>
          </DocCard>

          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Packet Loss</h3>
            <p className={docsColors.text.body}>
              Detects if packets are being dropped between your monitoring location and the target.
              High packet loss indicates network problems.
            </p>
          </DocCard>

          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
              Network Path Availability
            </h3>
            <p className={docsColors.text.body}>
              Confirms that the entire network path between the monitor and target is functional,
              including routers, switches, and firewalls.
            </p>
          </DocCard>
        </div>
      </DocSection>

      {/* Use Cases */}
      <DocSection title="Common Use Cases">
        <div className="space-y-4">
          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
              Infrastructure Monitoring
            </h3>
            <ul className={`space-y-2 ${docsColors.text.body}`}>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                Physical servers in data centers
              </li>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                Network devices (routers, switches, firewalls)
              </li>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                Virtual machines and cloud instances
              </li>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                IoT devices and embedded systems
              </li>
            </ul>
          </DocCard>

          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>Network Monitoring</h3>
            <ul className={`space-y-2 ${docsColors.text.body}`}>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                WAN link availability between offices
              </li>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                Internet connectivity verification
              </li>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                VPN tunnel endpoint monitoring
              </li>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                ISP connection reliability
              </li>
            </ul>
          </DocCard>

          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>Basic Health Checks</h3>
            <ul className={`space-y-2 ${docsColors.text.body}`}>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                Quick verification that a host is online
              </li>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                Lightweight monitoring for non-critical systems
              </li>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                Backup connectivity test when services are down
              </li>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                Initial troubleshooting step for connectivity issues
              </li>
            </ul>
          </DocCard>
        </div>
      </DocSection>

      {/* Advantages and Limitations */}
      <DocSection title="Advantages and Limitations">
        <div className="grid md:grid-cols-2 gap-6">
          <DocAlert variant="success" className="p-6">
            <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>
              ✓ Advantages
            </h3>
            <ul className={`space-y-2 ${docsColors.text.body}`}>
              <li className="flex items-start">
                <span className="text-green-400 mr-2">•</span>
                Works with any IP-enabled device
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-2">•</span>
                No specific ports or services required
              </li>
              <li className="flex items-start">
                <span className="text-green-400 mr-2">•</span>
                Very low overhead and resource usage
              </li>
            </ul>
          </DocAlert>

          <DocAlert variant="warning" className="p-6">
            <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>
              ⚠️ Limitations
            </h3>
            <ul className={`space-y-2 ${docsColors.text.body}`}>
              <li className="flex items-start">
                <span className="text-yellow-400 mr-2">•</span>
                Many cloud providers and hosting services disable ICMP by default for security
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 mr-2">•</span>
                Only tests basic network connectivity - a successful ping doesn&apos;t mean your
                services are working
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 mr-2">•</span>
                Corporate networks often block ICMP traffic at the firewall level
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 mr-2">•</span>
                Provides minimal diagnostic information when issues occur
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 mr-2">•</span>
                Cannot detect application-level problems like database failures or web server errors
              </li>
            </ul>
          </DocAlert>
        </div>
      </DocSection>

      {/* Best Practices */}
      <DocSection title="Best Practices">
        <DocCard>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>
                  Combine with service-specific monitoring
                </strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Use ping as a basic check, but also monitor specific services (HTTP, TCP) for
                  complete coverage
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Monitor from multiple locations</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Distinguish between local network issues and actual host problems
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Set reasonable thresholds</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Allow for occasional packet loss; don&apos;t alert on single failed pings
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Ensure ICMP is allowed</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Verify firewalls permit ICMP echo requests and replies
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Use for trend analysis</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Track response time trends to identify gradual network degradation
                </p>
              </div>
            </li>
          </ul>
        </DocCard>
      </DocSection>

      {/* Firewall Considerations */}
      <DocSection title="Firewall Considerations">
        <DocAlert variant="warning" className="p-6 ${docsColors.text.body}">
          <p className="mb-4">
            Many firewalls block ICMP traffic by default. To use ping monitoring effectively:
          </p>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="mr-2">1.</span>
              Allow ICMP echo requests (type 8) from our monitoring IP addresses
            </li>
            <li className="flex items-start">
              <span className="mr-2">2.</span>
              Allow ICMP echo replies (type 0) back to our monitors
            </li>
            <li className="flex items-start">
              <span className="mr-2">3.</span>
              Consider rate limiting instead of blocking to prevent abuse
            </li>
            <li className="flex items-start">
              <span className="mr-2">4.</span>
              Use our{" "}
              <DocLink href="https://uptime-monitor.io/web_api/checker_ip_addresses" external>
                IP whitelist endpoint
              </DocLink>{" "}
              to get current monitor addresses
            </li>
          </ul>
        </DocAlert>
      </DocSection>

      <DocNavFooter
        prev={{ href: "/docs/monitors/tcp", text: "TCP Port Monitoring" }}
        next={{ href: "/docs/notifications", text: "Notification Methods" }}
      />
    </DocLayout>
  );
}
